import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, Search, Users, Coins, BarChart2, TrendingUp } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import logo from '../assets/images/logo.png';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const navigationItems = [
    { path: '/', label: 'Home' },
    { path: '/properties', label: 'Properties' },
    { 
      path: '/buildfund-hub', 
      label: 'BuildFund Hub',
      isNew: true,
      subItems: [
        {
          path: '/buildfund-hub/community-pulse',
          label: 'CommunityPulse',
          icon: Users,
          description: 'Shape your community\'s future'
        },
        {
          path: '/buildfund-hub/smart-vest',
          label: 'SmartVest',
          icon: Coins,
          description: 'Smart real estate investment from ₦1,000'
        },
        {
          path: '/buildfund-hub/revenue-sense',
          label: 'RevenueSense',
          icon: BarChart2,
          description: 'RevenueSense insights'
        }
      ]
    },
    { 
      path: '/infrastructure', 
      label: 'Infrastructure',
      subItems: [
        {
          path: '/infrastructure/analysis',
          label: 'Analysis',
          icon: BarChart2,
          description: 'Infrastructure scoring and insights'
        },
        {
          path: '/infrastructure/market',
          label: 'Market Trends',
          icon: TrendingUp,
          description: 'Real estate market analysis'
        }
      ]
    }
  ];

  const isActive = (path) => location.pathname === path;
  const isActiveParent = (item) => {
    if (item.subItems) {
      return item.subItems.some(subItem => location.pathname.startsWith(subItem.path));
    }
    return location.pathname === item.path;
  };

  const handleNavigation = (item) => {
    if (item.subItems) {
      navigate(item.path);
    } else {
      navigate(item.path);
    }
    setIsMenuOpen(false);
  };

  return (
    <header className="bg-white shadow-sm fixed w-full top-0 z-50">
      <nav className="container mx-auto px-4">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center">
            <img 
              src={logo}
              alt="FastFind360 Logo" 
              className="h-12 w-auto"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-1">
            {navigationItems.map((item) => (
              <div key={item.path} className="relative group">
                <button
                  onClick={() => handleNavigation(item)}
                  className={`px-4 py-2 rounded-full font-medium transition-all duration-300 flex items-center gap-2
                    ${isActive(item.path)
                      ? 'text-[#1c5bde] bg-[#1c5bde]/5'
                      : 'text-gray-600 hover:text-[#1c5bde] hover:bg-[#1c5bde]/5'
                    }`}
                >
                  {item.label}
                  {item.isNew && (
                    <span className="absolute -top-1 -right-1 px-1.5 py-0.5 bg-[#ff8533] text-white text-xs rounded-full">
                      New
                    </span>
                  )}
                </button>
                
                {/* Dropdown Menu */}
                {item.subItems && (
                  <div className="absolute left-0 mt-2 w-64 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300">
                    <div className="bg-white rounded-xl shadow-lg p-3 space-y-1">
                      {item.subItems.map((subItem) => (
                        <Link
                          key={subItem.path}
                          to={subItem.path}
                          className={`flex items-start p-3 rounded-lg hover:bg-gray-50 transition-colors
                            ${isActive(subItem.path) ? 'bg-gray-50' : ''}`}
                        >
                          <subItem.icon className={`h-5 w-5 mt-0.5 ${
                            isActive(subItem.path) ? 'text-[#1c5bde]' : 'text-gray-400'
                          }`} />
                          <div className="ml-3">
                            <div className={`font-medium ${
                              isActive(subItem.path) ? 'text-[#1c5bde]' : 'text-gray-900'
                            }`}>
                              {subItem.label}
                            </div>
                            <div className="text-sm text-gray-500">{subItem.description}</div>
                          </div>
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Right Side Actions */}
          <div className="hidden md:flex items-center gap-2">
            <button className="p-2 hover:bg-gray-100 rounded-full transition-colors">
              <Search className="h-5 w-5 text-gray-600" />
            </button>
            <Link
              to="/signin"
              className="px-4 py-2 text-[#1c5bde] hover:bg-[#1c5bde]/5 rounded-full transition-colors"
            >
              Sign In
            </Link>
            <Link
              to="/signup"
              className="px-4 py-2 bg-[#1c5bde] text-white rounded-full hover:bg-[#0c0d8a] transition-colors"
            >
              Get Started
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button 
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="md:hidden p-2"
          >
            {isMenuOpen ? <X /> : <Menu />}
          </button>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {isMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="md:hidden border-t"
            >
              <div className="py-4 space-y-1">
                {navigationItems.map((item) => (
                  <div key={item.path}>
                    <button
                      onClick={() => {
                        handleNavigation(item);
                        setIsMenuOpen(false);
                      }}
                      className={`w-full px-4 py-2 text-left ${
                        isActiveParent(item)
                          ? 'text-[#1c5bde] bg-[#1c5bde]/5'
                          : 'text-gray-600'
                      }`}
                    >
                      {item.label}
                      {item.isNew && (
                        <span className="ml-2 px-1.5 py-0.5 bg-red-500 text-white text-xs rounded-full">
                          New
                        </span>
                      )}
                    </button>
                    {item.subItems && (
                      <div className="pl-8 space-y-1 py-2">
                        {item.subItems.map((subItem) => (
                          <Link
                            key={subItem.path}
                            to={subItem.path}
                            onClick={() => setIsMenuOpen(false)}
                            className={`flex items-center gap-2 px-4 py-2 rounded-lg
                              ${isActive(subItem.path)
                                ? 'text-[#1c5bde] bg-[#1c5bde]/5'
                                : 'text-gray-600 hover:text-[#1c5bde] hover:bg-[#1c5bde]/5'
                              }`}
                          >
                            <subItem.icon className="h-4 w-4" />
                            {subItem.label}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
                
                {/* Add Mobile Auth Buttons */}
                <div className="pt-4 mt-4 border-t space-y-2 px-4">
                  <Link
                    to="/signin"
                    onClick={() => setIsMenuOpen(false)}
                    className="block w-full py-2 text-center text-[#1c5bde] hover:bg-[#1c5bde]/5 rounded-full transition-colors"
                  >
                    Sign In
                  </Link>
                  <Link
                    to="/signup"
                    onClick={() => setIsMenuOpen(false)}
                    className="block w-full py-2 text-center bg-[#1c5bde] text-white rounded-full hover:bg-[#0c0d8a] transition-colors"
                  >
                    Get Started
                  </Link>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>
    </header>
  );
};

export default Header; 